import {Router} from '@angular/router';
import {map, startWith} from 'rxjs/operators';
import {Logger} from '../utils/Logger';
import {HttpClient} from '@angular/common/http';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {ApiServiceService} from '../api-service.service';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {BankService} from '../service/bank.service';
import {BadRequestError} from '../errors/BadRequestError';
import {Toaster, Type} from '../toaster/Toaster';
import {Observable} from 'rxjs';

export interface BankFormData {
    account_no: string;
    account_type: string;
    confirm_account_no: string;
    bank_name: string;
    bank_branch_name: string;
    bank_city: string;
    bank_ifsc: string;
    account_holder_name: string;
}

@Component({
    selector: 'app-lenderbankdetail',
    templateUrl: './lender-bank-detail.component.html',
    styleUrls: ['./lender-bank-detail.component.css'],
    providers: [ApiServiceService, BankService]
})

export class LenderBankDetailComponent implements OnInit, OnChanges {
    bankName: string;
    accountHolderName: string;
    branchName: string;
    accountType: string;
    ifsc: string;
    accountNumber: string;
    reAccountNumber: string;
    city: string;

    bankList: string [];
    bankBranchList: any;
    cityList: any;
    bankAccountDetails = false;

    cityreadonly = true;
    branchreadonly = true;

    finzyDomain = true;
    otherDomain = false;

    bankNameFormControl: FormControl = new FormControl(undefined, Validators.required);
    cityFormControl: FormControl = new FormControl(undefined, Validators.required);
    branchFormControl: FormControl = new FormControl(undefined, Validators.required);
    filteredBankNames: Observable<string[]>;
    filteredCities: Observable<string[]>;
    filteredBranches: Observable<string[]>;

    accountTypes = [
        {value: 'saving', viewValue: 'Saving'},
        {value: 'current', viewValue: 'Current'}
    ];
    loadingCities: boolean;
    loadingBranches: boolean;

    public readonly searchByBankFormGroup: FormGroup;
    public readonly searchByIfscFormGroup: FormGroup;

    @Input('back-button-link')
    public readonly backButtonLink: string;

    @Input('pre-filled-bank-data')
    public readonly preFilledBankData: any;

    @Output('on-bank-details-submit')
    onSubmit: EventEmitter<BankFormData> = new EventEmitter<BankFormData>();
    public ifscFormControl: FormControl;

    @Output()
    public readonly onSearchBankByIfsc = new EventEmitter();

    @Output()
    public readonly onNext = new EventEmitter();

    constructor(private httpClient: HttpClient, private router: Router, private ref: ApiServiceService,
                private bankService: BankService, formBuilder: FormBuilder) {
        const accountNumberFormControl = new FormControl(undefined, Validators.required);
        const repeatedAccountNumberFormControl = new FormControl(undefined, Validators.required);
        this.ifscFormControl = new FormControl(undefined, [Validators.required, Validators.pattern('[a-zA-Z]{4}[a-zA-Z0-9]{7}')]);
        this.searchByBankFormGroup = formBuilder.group({
            bankName: this.bankNameFormControl,
            city: this.cityFormControl,
            branch: this.branchFormControl,
            ifsc: this.ifscFormControl,
            accountHolderName: new FormControl(undefined, Validators.required),
            accountType: new FormControl(undefined, Validators.required),
            accountNumberGroup: formBuilder.group({
                accountNumber: accountNumberFormControl,
                repeatedAccountNumber: repeatedAccountNumberFormControl
            }, {validator: LenderBankDetailComponent.validateRepeatedAccountNumber})
        });
        this.searchByIfscFormGroup = this.searchByBankFormGroup;
    }

    private static getErrorMessagesForSearchingBankByIfscAccountForm(): any {
        return {
            bankName: [{
                type: 'required',
                message: 'Please select a bank'
            }],
            city: [{
                type: 'required',
                message: 'Please select a city'
            }],
            branch: [{
                type: 'required',
                message: 'Please select a branch'
            }],
            ifsc: [{
                type: 'required',
                message: 'Please provide an IFSC number'
            }, {
                type: 'pattern',
                message: 'Please enter a valid IFSC code'
            }, {
                type: 'notExist',
                message: 'The IFSC code you have entered does not exist'
            }],
            accountHolderName: [{
                type: 'required',
                message: 'Please enter your name as mentioned in the bank account'
            }],
            accountType: [{
                type: 'required',
                message: 'Please select the account type'
            }],
            accountNumber: [{
                type: 'required',
                message: 'Please provide a valid account number'
            }],
            repeatedAccountNumber: [{
                type: 'required',
                message: 'Please provide a valid account number'
            }],
            accountNumberGroup: [{
                type: 'match',
                message: 'Account Numbers do not match. Please provide the same account number in both the fields.'
            }]
        };
    }

    private static validateRepeatedAccountNumber(control: AbstractControl): ValidationErrors | null {
        const accountNumberFormControl = control.get('accountNumber');
        const repeatedAccountNumberFormControl = control.get('repeatedAccountNumber');
        const originalValue = accountNumberFormControl.value;
        const repeatedValue = repeatedAccountNumberFormControl.value;
        if (repeatedValue !== originalValue) {
            return {match: true};
        } else {
            return null;
        }
    }

    onTabChange(tabIndex: number) {
        if (tabIndex === 1) {
            this.onSearchBankByIfsc.emit();
        }
    }

    ngOnInit() {
        this.httpClient.post(this.ref.baseURL() + 'bankLists', {})
            .toPromise()
            .then((bankListResponse: any) => {
                this.bankList = bankListResponse.result.bank_list.map(function (obj) {
                    return obj.bank_name;
                });
                this.filteredBankNames =
                    this.bankNameFormControl.valueChanges.pipe(startWith(''), map(val => this.filterBankNames(val)));
            })
            .catch(error => {
                Logger.error(`An error occurred while fetching and refreshing bank list: ${error}`);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.preFilledBankData) {
            const changedProperty = changes.preFilledBankData;
            if (changedProperty.currentValue) {
                this.populateFilledBankInfo(this.preFilledBankData);
            }
        }
    }

    onBankNameSelected(selectedBankName) {
        if (selectedBankName && selectedBankName.length > 5) {
            this.bankName = selectedBankName;
            this.city = this.ifsc = this.branchName = this.accountType = this.accountNumber = this.reAccountNumber = undefined;
            this.cityreadonly = false;
            this.loadingCities = true;

            this.httpClient.post(this.ref.baseURL() + 'searchBankCity', {bank_name: this.bankName})
                .toPromise()
                .then((bankCityResponse: any) => {
                    this.cityList = bankCityResponse.result.map(function (obj) {
                        return obj.city_name;
                    });
                    this.loadingCities = false;
                })
                .catch(error => {
                    Logger.error(`An error occurred while fetching and refreshing bank cities: ${error}`);
                    this.loadingCities = false;
                });
        }
    }
    public getCityName(){
        let citySearch = this.cityFormControl.value;

        if(citySearch?.length < 3) {
           this.filteredCities = null;
        }
        if(citySearch?.length === 3) {
            this.filteredCities = this.cityFormControl.valueChanges
                .pipe(startWith(citySearch), map(val => this.filterCities(val)));
        }
    }

    onCitySelected(city) {
        if (city && city.length > 5) {
            this.city = city;
            this.ifsc = this.branchName = this.accountType = this.accountNumber = this.reAccountNumber = undefined;
            this.cityreadonly = false;
            this.branchreadonly = false;
            this.loadingBranches = true;

            if (this.bankName && city) {
                this.httpClient.post(this.ref.baseURL() + 'getBankBranch',
                    {
                        bank_name: this.bankName,
                        city: city
                    }).toPromise()
                    .then((bankBranchResponse: any) => {
                        this.bankBranchList = bankBranchResponse.result.map(function (obj) {
                            return obj.branch_name;
                        });
                        this.filteredBranches = this.branchFormControl.valueChanges
                            .pipe(startWith(''), map(val => this.filterBranches(val)));
                        this.loadingBranches = false;
                    })
                    .catch(error => {
                        Logger.log(`An error occurred while fetching and refreshing branch names: ${error}`);
                        this.bankBranchList = '';
                        this.ifsc = '';
                        this.loadingBranches = false;
                    });
            }
        }
    }

    onBranchSelected(selectedBranch) {
        if (selectedBranch && selectedBranch.length > 5) {
            this.branchName = selectedBranch;
            if (this.branchName) {
                this.httpClient.post(this.ref.baseURL() + 'getBankIfcs',
                    {
                        branch_name: this.branchName,
                        bank_name: this.bankName
                    }).toPromise()
                    .then((ifscCodeResponse: any) => {
                        this.bankAccountDetails = true;
                        this.ifsc = ifscCodeResponse.result.IFSC;
                    })
                    .catch(error => {
                        Logger.log(`An error occurred while fetching and refreshing IFSC codes: ${error}`);
                        this.ifsc = '';
                    });
            }
        }
    }

    bankAccNum1(event) {
        this.accountNumber = event.target.value.replace(/[^0-9]+/, '');
    }

    bankAccNum2(event) {
        this.reAccountNumber = event.target.value.replace(/[^0-9]+/, '');
    }

    bankIfsc(event) {
        this.ifsc = event.target.value.replace(/[^0-9A-Za-z]+/, '');
    }

    submitBankForm(formGroup: FormGroup) {
        FormGroupUtil.validateFormGroup(formGroup);

        if (formGroup.invalid) {
            Logger.log(`The form is invalid. Not proceeding with the event.`);
        } else {
            const enteredFormData = formGroup.value;
            this.onSubmit.emit(this.createBankFormData(enteredFormData));
        }
    }

    async searchViaIfsc(ifsc: string): Promise<void> {
        try {
            if (this.ifscFormControl.valid) {
                const bank = await this.bankService.findByIfsc(ifsc.toUpperCase());
                this.bankName = bank.name;
                this.branchName = bank.branch;
                this.city = bank.city;
                this.bankAccountDetails = true;
            }
        } catch (error) {
            if (error instanceof BadRequestError) {
                this.searchByIfscFormGroup.controls.ifsc.setErrors({'notExist': 0});
            } else {
                Toaster.toast('Something went wrong while getting your Bank details. Please try again later.', Type.Error);
                throw Error('Something went wrong');
            }
        }
    }

    clearPreFilledDetails(): void {
        this.bankName = '';
        this.branchName = '';
        this.city = '';
        this.bankAccountDetails = false;
    }

    public getErrorMessage(control: AbstractControl): string {
        const controlName = this.getControlName(control);
        let errors;
        switch (control.root) {
            case this.searchByBankFormGroup:
                errors = LenderBankDetailComponent.getErrorMessagesForSearchingBankByIfscAccountForm();
                break;
            case this.searchByIfscFormGroup:
                errors = LenderBankDetailComponent.getErrorMessagesForSearchingBankByIfscAccountForm();
                break;
            default:
                Logger.log(`I don't know how to handle this control ${control}`);
                return;
        }
        let message = '';
        if (control.errors) {
            Object.keys(control.errors).forEach(occurredError => {
                const predefinedErrorsForControl = errors[controlName];
                const result = predefinedErrorsForControl.find(predefinedError => predefinedError.type === occurredError);
                if (result) {
                    message += result.message;
                } else {
                    Logger.log(`Could not find error message for error: ${occurredError}`);
                }
            });
        }
        return message;
    }

    private createBankFormData(enteredFormData): BankFormData {
        return {
            account_no: enteredFormData.accountNumberGroup.accountNumber,
            account_type: enteredFormData.accountType,
            confirm_account_no: enteredFormData.accountNumberGroup.repeatedAccountNumber,
            bank_name: enteredFormData.bankName,
            bank_branch_name: enteredFormData.branch,
            bank_city: enteredFormData.city,
            bank_ifsc: enteredFormData.ifsc.toUpperCase(),
            account_holder_name: enteredFormData.accountHolderName
        };
    }

    private getControlName(c: AbstractControl): string | null {
        const formGroup = c.parent.controls;
        return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
    }

    private filterBankNames(val: string): string[] {
        return this.bankList.filter(bankName => bankName.toLowerCase().startsWith(val.toLowerCase()));
    }

    private filterCities(val: string): string[] {
        return this.cityList.filter(cname => cname.toLowerCase().startsWith(val.toLowerCase())).slice(0, 20);
    }

    private filterBranches(val: string): string[] {
        return this.bankBranchList.filter(brname => brname.toLowerCase().startsWith(val.toLowerCase())).slice(0, 20);
    }

    private async populateFilledBankInfo(preFilledBankData: any) {
        this.bankAccountDetails = true;
        const bankAccount = preFilledBankData;
        this.bankName = bankAccount.bank_name;
        this.ifsc = bankAccount.ifsc;
        this.accountType = bankAccount.account_type;
        this.reAccountNumber = bankAccount.account_no;
        this.accountNumber = bankAccount.account_no;
        this.accountHolderName = bankAccount.accountHolderName;

        const bankCityResponse: any = this.httpClient.post(this.ref.baseURL() + 'searchBankCity',
            {bank_name: this.bankName})
            .toPromise();
        this.cityList = bankCityResponse.result;
        this.city = bankAccount.branch_city;

        const responseBody: any = this.httpClient.post(this.ref.baseURL() + 'getBankBranch',
            {
                bank_name: this.bankName,
                city: this.city
            }).toPromise();
        this.bankBranchList = responseBody.result;
        this.branchName = bankAccount.branch_name;
    }
}
